import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useItemHover } from './useItemHover'
import {
  closeSideBar,
  openSideBar as openSideBarAction,
  MIN_WIDTH_FOR_STICKY_SIDEBAR,
  selectIsSideBarSticky,
  selectStickyItemId,
} from '../../slice'
import { uiSlice } from '@wellesley/ui/slices/uiSlice'

type UseSideBarVisibleProps = {
  itemId?: string
  canStick: boolean
  onHover?: (isHovered: boolean) => void
  onClick?: () => void
}
export const useSideBarVisible = ({
  itemId,
  canStick,
  onHover,
  onClick,
}: UseSideBarVisibleProps) => {
  const dispatch = useDispatch()
  const deviceWidth = useSelector(uiSlice.selectors.selectDeviceWidth)
  const isSideBarSticky = useSelector(selectIsSideBarSticky)
  const stickyItemId = useSelector(selectStickyItemId)
  const close = useCallback(() => {
    dispatch(closeSideBar())
  }, [dispatch])
  const {
    ref: hoverRef,
    setIsHoverOpen,
    isHoverOpen,
  } = useItemHover({
    onHover,
  })

  const hasSpaceForFixed =
    deviceWidth !== null && deviceWidth >= MIN_WIDTH_FOR_STICKY_SIDEBAR
  const isCurrentItemSticky = isSideBarSticky && stickyItemId === itemId
  const isSideBarOpen = isCurrentItemSticky || isHoverOpen
  const isItemSticky =
    isCurrentItemSticky || (canStick && hasSpaceForFixed && isHoverOpen)

  const isAutoOpenAllowed = useRef(true)
  useEffect(() => {
    if (canStick) {
      setIsHoverOpen(false)
    }
  }, [canStick, setIsHoverOpen])

  useEffect(() => {
    if (isCurrentItemSticky && !hasSpaceForFixed) {
      dispatch(closeSideBar())
    }
  }, [isCurrentItemSticky, hasSpaceForFixed, dispatch])

  useEffect(() => {
    if (!canStick || !hasSpaceForFixed || !itemId) {
      isAutoOpenAllowed.current = true
      return
    }
    if (isAutoOpenAllowed.current && !isCurrentItemSticky && !isSideBarSticky) {
      dispatch(openSideBarAction(itemId))
    }
    isAutoOpenAllowed.current = false
  }, [
    canStick,
    hasSpaceForFixed,
    isCurrentItemSticky,
    isSideBarSticky,
    itemId,
    dispatch,
  ])

  const openSideBar = useCallback(() => {
    if ((hasSpaceForFixed || isSideBarSticky) && itemId) {
      dispatch(openSideBarAction(itemId))
      isAutoOpenAllowed.current = false
    }
    onClick?.()
  }, [onClick, hasSpaceForFixed, isSideBarSticky, itemId, dispatch])

  return {
    hoverRef,
    isSideBarOpen,
    isItemSticky,
    openSideBar,
    close,
  }
}
